<template>
  <div class="webhooks-manager">
    <!-- Header -->
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col">
          <h4 class="font-weight-bolder mb-1">CONECTORES API</h4>
          <p class="text-muted mb-0">Gestiona webhooks y conexiones con APIs externas</p>
        </div>
        <div class="col-auto">
          <button 
            class="btn btn-primary btn-sm" 
            @click="showCreateModal = true"
          >
            <i class="fa fa-plus mr-1"></i>
            Nuevo Webhook
          </button>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <card class="mb-4">
      <div class="row">
        <div class="col-md-4">
          <div class="form-group mb-0">
            <label>Buscar</label>
            <input 
              type="text" 
              class="form-control form-control-sm" 
              placeholder="Nombre, URL o descripción..."
              v-model="filters.search"
            >
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group mb-0">
            <label>Estado</label>
            <el-select 
              v-model="filters.status" 
              placeholder="Todos" 
              size="small" 
              class="w-100"
            >
              <el-option label="Todos" value=""></el-option>
              <el-option label="Activo" value="active"></el-option>
              <el-option label="Inactivo" value="inactive"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group mb-0">
            <label>Tipo</label>
            <el-select 
              v-model="filters.type" 
              placeholder="Todos" 
              size="small" 
              class="w-100"
            >
              <el-option label="Todos" value=""></el-option>
              <el-option label="Webhook Entrante" value="incoming"></el-option>
              <el-option label="Webhook Saliente" value="outgoing"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-group mb-0">
            <label>Acceso</label>
            <el-select 
              v-model="filters.isPublic" 
              placeholder="Todos" 
              size="small" 
              class="w-100"
            >
              <el-option label="Todos" value=""></el-option>
              <el-option label="Público" value="true"></el-option>
              <el-option label="Privado" value="false"></el-option>
            </el-select>
          </div>
        </div>
      </div>
    </card>

    <!-- Webhooks Table -->
    <card>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th width="5%">
                <input 
                  type="checkbox" 
                  v-model="selectAll" 
                  @change="toggleSelectAll"
                >
              </th>
              <th width="20%">Nombre</th>
              <th width="25%">URL</th>
              <th width="10%">Tipo</th>
              <th width="10%">Estado</th>
              <th width="10%">Acceso</th>
              <th width="15%">Última Ejecución</th>
              <th width="15%">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-4">
                <i class="fa fa-spinner fa-spin mr-2"></i>
                Cargando webhooks...
              </td>
            </tr>
            <tr v-else-if="filteredWebhooks.length === 0">
              <td colspan="8" class="text-center py-4 text-muted">
                <i class="fa fa-inbox fa-2x mb-2 d-block"></i>
                No se encontraron webhooks
              </td>
            </tr>
            <tr v-else v-for="webhook in paginatedWebhooks" :key="webhook.id">
              <td>
                <input 
                  type="checkbox" 
                  v-model="selectedWebhooks" 
                  :value="webhook.id"
                >
              </td>
              <td>
                <div>
                  <strong>{{ webhook.name }}</strong>
                  <br>
                  <small class="text-muted">{{ webhook.description || 'Sin descripción' }}</small>
                </div>
              </td>
              <td>
                <code class="small">{{ webhook.url }}</code>
              </td>
              <td>
                <span 
                  class="badge badge-sm"
                  :class="webhook.type === 'incoming' ? 'badge-info' : 'badge-warning'"
                >
                  {{ webhook.type === 'incoming' ? 'Entrante' : 'Saliente' }}
                </span>
              </td>
              <td>
                <p-switch
                  type="success"
                  size="small"
                  v-model="webhook.isActive"
                  @change="toggleWebhookStatus(webhook)"
                />
              </td>
              <td>
                <span 
                  class="badge badge-sm"
                  :class="webhook.isPublic ? 'badge-success' : 'badge-secondary'"
                >
                  {{ webhook.isPublic ? 'Público' : 'Privado' }}
                </span>
              </td>
              <td>
                <div v-if="webhook.lastExecution">
                  <small>{{ formatDate(webhook.lastExecution.date) }}</small>
                  <br>
                  <span 
                    class="badge badge-xs"
                    :class="getStatusBadgeClass(webhook.lastExecution.status)"
                  >
                    {{ webhook.lastExecution.status }}
                  </span>
                </div>
                <small v-else class="text-muted">Nunca ejecutado</small>
              </td>
              <td>
                <div class="btn-group btn-group-sm">
                  <button 
                    class="btn btn-outline-info btn-sm" 
                    @click="testWebhook(webhook)"
                    :disabled="!webhook.isActive"
                    title="Probar webhook"
                  >
                    <i class="fa fa-play"></i>
                  </button>
                  <button 
                    class="btn btn-outline-primary btn-sm" 
                    @click="editWebhook(webhook)"
                    title="Editar"
                  >
                    <i class="fa fa-edit"></i>
                  </button>
                  <button 
                    class="btn btn-outline-secondary btn-sm" 
                    @click="duplicateWebhook(webhook)"
                    title="Duplicar"
                  >
                    <i class="fa fa-copy"></i>
                  </button>
                  <button 
                    class="btn btn-outline-danger btn-sm" 
                    @click="deleteWebhook(webhook.id)"
                    title="Eliminar"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalPages > 1">
        <div>
          Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }} - 
          {{ Math.min(currentPage * itemsPerPage, filteredWebhooks.length) }} 
          de {{ filteredWebhooks.length }} webhooks
        </div>
        <el-pagination
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="itemsPerPage"
          :total="filteredWebhooks.length"
          layout="prev, pager, next"
          small
        />
      </div>

      <!-- Bulk Actions -->
      <div class="mt-3" v-if="selectedWebhooks.length > 0">
        <div class="alert alert-info">
          <strong>{{ selectedWebhooks.length }}</strong> webhook(s) seleccionado(s)
          <div class="mt-2">
            <button class="btn btn-sm btn-success mr-2" @click="bulkActivate">
              <i class="fa fa-check mr-1"></i>Activar
            </button>
            <button class="btn btn-sm btn-warning mr-2" @click="bulkDeactivate">
              <i class="fa fa-pause mr-1"></i>Desactivar
            </button>
            <button class="btn btn-sm btn-danger" @click="bulkDelete">
              <i class="fa fa-trash mr-1"></i>Eliminar
            </button>
          </div>
        </div>
      </div>
    </card>

    <!-- Create/Edit Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showCreateModal || showEditModal }" 
      :style="{ display: (showCreateModal || showEditModal) ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ showEditModal ? 'Editar Webhook' : 'Crear Nuevo Webhook' }}
            </h5>
            <button type="button" class="close" @click="closeModal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveWebhook">
              <!-- Basic Information -->
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label>Nombre <span class="text-danger">*</span></label>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="currentWebhook.name"
                      :class="{ 'is-invalid': !currentWebhook.name }"
                      required
                    >
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Tipo <span class="text-danger">*</span></label>
                    <el-select v-model="currentWebhook.type" class="w-100">
                      <el-option label="Webhook Entrante" value="incoming"></el-option>
                      <el-option label="Webhook Saliente" value="outgoing"></el-option>
                    </el-select>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>Descripción</label>
                <textarea 
                  class="form-control" 
                  rows="2" 
                  v-model="currentWebhook.description"
                  placeholder="Breve descripción del webhook..."
                ></textarea>
              </div>

              <div class="form-group">
                <label>URL <span class="text-danger">*</span></label>
                <input 
                  type="url" 
                  class="form-control" 
                  v-model="currentWebhook.url"
                  :class="{ 'is-invalid': !currentWebhook.url }"
                  :placeholder="currentWebhook.type === 'incoming' ? 'URL donde recibirás los datos' : 'URL externa a la que enviarás datos'"
                  required
                >
              </div>

              <!-- HTTP Method (for outgoing) -->
              <div class="row" v-if="currentWebhook.type === 'outgoing'">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Método HTTP</label>
                    <el-select v-model="currentWebhook.method" class="w-100">
                      <el-option label="POST" value="POST"></el-option>
                      <el-option label="PUT" value="PUT"></el-option>
                      <el-option label="PATCH" value="PATCH"></el-option>
                      <el-option label="GET" value="GET"></el-option>
                    </el-select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Timeout (segundos)</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="currentWebhook.timeout"
                      min="1"
                      max="300"
                    >
                  </div>
                </div>
              </div>

              <!-- Configuration Tabs -->
              <div class="nav nav-tabs nav-fill" role="tablist">
                <a 
                  class="nav-item nav-link"
                  :class="{ active: activeTab === 'config' }"
                  @click="activeTab = 'config'"
                >
                  <i class="fa fa-cog mr-1"></i>Configuración
                </a>
                <a 
                  class="nav-item nav-link"
                  :class="{ active: activeTab === 'headers' }"
                  @click="activeTab = 'headers'"
                >
                  <i class="fa fa-list mr-1"></i>Headers
                </a>
                <a 
                  class="nav-item nav-link"
                  :class="{ active: activeTab === 'variables' }"
                  @click="activeTab = 'variables'"
                >
                  <i class="fa fa-code mr-1"></i>Variables
                </a>
                <a 
                  class="nav-item nav-link"
                  :class="{ active: activeTab === 'events' }"
                  @click="activeTab = 'events'"
                >
                  <i class="fa fa-bolt mr-1"></i>Eventos
                </a>
              </div>

              <div class="tab-content mt-3">
                <!-- Configuration Tab -->
                <div v-if="activeTab === 'config'" class="tab-pane active">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <div class="d-flex align-items-center">
                          <p-switch
                            type="success"
                            v-model="currentWebhook.isActive"
                          />
                          <label class="mb-0 ml-2">Webhook Activo</label>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <div class="d-flex align-items-center">
                          <p-switch
                            type="success"
                            v-model="currentWebhook.isPublic"
                          />
                          <label class="mb-0 ml-2">Acceso Público</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form-group" v-if="!currentWebhook.isPublic">
                    <label>Token de Autenticación</label>
                    <div class="input-group">
                      <input 
                        type="text" 
                        class="form-control" 
                        v-model="currentWebhook.authToken"
                        readonly
                      >
                      <div class="input-group-append">
                        <button 
                          type="button" 
                          class="btn btn-outline-secondary" 
                          @click="generateAuthToken"
                        >
                          <i class="fa fa-refresh"></i>
                        </button>
                      </div>
                    </div>
                    <small class="form-text text-muted">
                      Este token se usará para autenticar las peticiones al webhook
                    </small>
                  </div>

                  <div class="form-group">
                    <label>Reintentos en caso de fallo</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model.number="currentWebhook.retryAttempts"
                      min="0"
                      max="5"
                    >
                  </div>
                </div>

                <!-- Headers Tab -->
                <div v-if="activeTab === 'headers'" class="tab-pane active">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6>Headers HTTP</h6>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-primary" 
                      @click="addHeader"
                    >
                      <i class="fa fa-plus mr-1"></i>Agregar Header
                    </button>
                  </div>
                  
                  <div v-if="currentWebhook.headers.length === 0" class="text-center text-muted py-3">
                    <i class="fa fa-list fa-2x mb-2 d-block"></i>
                    No hay headers configurados
                  </div>

                  <div v-else>
                    <div 
                      v-for="(header, index) in currentWebhook.headers" 
                      :key="index"
                      class="row mb-2"
                    >
                      <div class="col-md-5">
                        <input 
                          type="text" 
                          class="form-control" 
                          placeholder="Header name"
                          v-model="header.key"
                        >
                      </div>
                      <div class="col-md-6">
                        <input 
                          type="text" 
                          class="form-control" 
                          placeholder="Header value"
                          v-model="header.value"
                        >
                      </div>
                      <div class="col-md-1">
                        <button 
                          type="button" 
                          class="btn btn-outline-danger btn-sm" 
                          @click="removeHeader(index)"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Variables Tab -->
                <div v-if="activeTab === 'variables'" class="tab-pane active">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6>Estructura de Variables</h6>
                      <small class="text-muted">
                        Define las variables que se {{ currentWebhook.type === 'incoming' ? 'recibirán' : 'enviarán' }}
                      </small>
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-primary" 
                      @click="addVariable"
                    >
                      <i class="fa fa-plus mr-1"></i>Agregar Variable
                    </button>
                  </div>

                  <div v-if="currentWebhook.variables.length === 0" class="text-center text-muted py-3">
                    <i class="fa fa-code fa-2x mb-2 d-block"></i>
                    No hay variables configuradas
                  </div>

                  <div v-else>
                    <div 
                      v-for="(variable, index) in currentWebhook.variables" 
                      :key="index"
                      class="card mb-2"
                    >
                      <div class="card-body p-3">
                        <div class="row">
                          <div class="col-md-4">
                            <label class="small">Nombre</label>
                            <input 
                              type="text" 
                              class="form-control form-control-sm" 
                              v-model="variable.name"
                            >
                          </div>
                          <div class="col-md-3">
                            <label class="small">Tipo</label>
                            <el-select 
                              v-model="variable.type" 
                              size="small" 
                              class="w-100"
                            >
                              <el-option label="String" value="string"></el-option>
                              <el-option label="Number" value="number"></el-option>
                              <el-option label="Boolean" value="boolean"></el-option>
                              <el-option label="Array" value="array"></el-option>
                              <el-option label="Object" value="object"></el-option>
                            </el-select>
                          </div>
                          <div class="col-md-4">
                            <label class="small">Descripción</label>
                            <input 
                              type="text" 
                              class="form-control form-control-sm" 
                              v-model="variable.description"
                            >
                          </div>
                          <div class="col-md-1">
                            <label class="small">&nbsp;</label>
                            <button 
                              type="button" 
                              class="btn btn-outline-danger btn-sm d-block" 
                              @click="removeVariable(index)"
                            >
                              <i class="fa fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <div class="row mt-2">
                          <div class="col-md-6">
                            <div class="form-check">
                              <input 
                                type="checkbox" 
                                class="form-check-input" 
                                v-model="variable.required"
                                :id="`required-${index}`"
                              >
                              <label 
                                class="form-check-label small" 
                                :for="`required-${index}`"
                              >
                                Obligatorio
                              </label>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <input 
                              type="text" 
                              class="form-control form-control-sm" 
                              placeholder="Valor por defecto"
                              v-model="variable.defaultValue"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Events Tab -->
                <div v-if="activeTab === 'events'" class="tab-pane active">
                  <h6>Eventos que Disparan el Webhook</h6>
                  <small class="text-muted d-block mb-3">
                    Selecciona los eventos del sistema que activarán este webhook
                  </small>

                  <div class="row">
                    <div class="col-md-6" v-for="event in availableEvents" :key="event.value">
                      <div class="form-check mb-2">
                        <input 
                          type="checkbox" 
                          class="form-check-input" 
                          v-model="currentWebhook.events"
                          :value="event.value"
                          :id="`event-${event.value}`"
                        >
                        <label 
                          class="form-check-label" 
                          :for="`event-${event.value}`"
                        >
                          <strong>{{ event.label }}</strong>
                          <br>
                          <small class="text-muted">{{ event.description }}</small>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveWebhook"
              :disabled="!isValidWebhook"
            >
              {{ showEditModal ? 'Actualizar' : 'Crear' }} Webhook
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div 
      class="modal-backdrop fade show" 
      v-if="showCreateModal || showEditModal"
      @click="closeModal"
    ></div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ConectoresAPI',
  data() {
    return {
      // UI State
      loading: false,
      showCreateModal: false,
      showEditModal: false,
      activeTab: 'config',
      selectAll: false,
      
      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
      
      // Filters
      filters: {
        search: '',
        status: '',
        type: '',
        isPublic: ''
      },
      
      // Data
      webhooks: [],
      selectedWebhooks: [],
      
      // Current webhook being edited/created
      currentWebhook: this.getEmptyWebhook(),
      
      // Available events
      availableEvents: [
        { 
          value: 'customer.created', 
          label: 'Cliente Creado', 
          description: 'Se ejecuta cuando se crea un nuevo cliente' 
        },
        { 
          value: 'customer.updated', 
          label: 'Cliente Actualizado', 
          description: 'Se ejecuta cuando se actualiza un cliente' 
        },
        { 
          value: 'membership.created', 
          label: 'Membresía Creada', 
          description: 'Se ejecuta cuando se crea una nueva membresía' 
        },
        { 
          value: 'membership.expired', 
          label: 'Membresía Vencida', 
          description: 'Se ejecuta cuando una membresía expira' 
        },
        { 
          value: 'sale.completed', 
          label: 'Venta Completada', 
          description: 'Se ejecuta cuando se completa una venta' 
        },
        { 
          value: 'payment.received', 
          label: 'Pago Recibido', 
          description: 'Se ejecuta cuando se recibe un pago' 
        },
      ]
    }
  },
  
  computed: {
    filteredWebhooks() {
      return this.webhooks.filter(webhook => {
        const matchesSearch = !this.filters.search || 
          webhook.name.toLowerCase().includes(this.filters.search.toLowerCase()) ||
          webhook.url.toLowerCase().includes(this.filters.search.toLowerCase()) ||
          (webhook.description && webhook.description.toLowerCase().includes(this.filters.search.toLowerCase()))
          
        const matchesStatus = !this.filters.status || 
          (this.filters.status === 'active' && webhook.isActive) ||
          (this.filters.status === 'inactive' && !webhook.isActive)
          
        const matchesType = !this.filters.type || webhook.type === this.filters.type
        
        const matchesAccess = !this.filters.isPublic || 
          webhook.isPublic.toString() === this.filters.isPublic
          
        return matchesSearch && matchesStatus && matchesType && matchesAccess
      })
    },
    
    paginatedWebhooks() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredWebhooks.slice(start, end)
    },
    
    totalPages() {
      return Math.ceil(this.filteredWebhooks.length / this.itemsPerPage)
    },
    
    isValidWebhook() {
      return this.currentWebhook.name && 
             this.currentWebhook.url && 
             this.currentWebhook.type
    }
  },
  
  mounted() {
    this.loadWebhooks()
  },
  
  methods: {
    getEmptyWebhook() {
      return {
        id: null,
        name: '',
        description: '',
        url: '',
        type: 'incoming',
        method: 'POST',
        isActive: true,
        isPublic: false,
        authToken: this.generateRandomToken(),
        timeout: 30,
        retryAttempts: 3,
        headers: [],
        variables: [],
        events: []
      }
    },
    
    async loadWebhooks() {
      this.loading = true
      try {
        const response = await axios.get('/api/webhooks')
        this.webhooks = response.data.data || []
      } catch (error) {
        console.error('Error loading webhooks:', error)
        this.$notify({
          title: 'Error',
          message: 'No se pudieron cargar los webhooks',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    
    async saveWebhook() {
      if (!this.isValidWebhook) return
      
      try {
        const webhookData = { ...this.currentWebhook }
        
        if (this.showEditModal) {
          await axios.put(`/api/webhooks/${webhookData.id}`, webhookData)
          this.$notify({
            title: 'Éxito',
            message: 'Webhook actualizado correctamente',
            type: 'success'
          })
        } else {
          delete webhookData.id
          await axios.post('/api/webhooks', webhookData)
          this.$notify({
            title: 'Éxito',
            message: 'Webhook creado correctamente',
            type: 'success'
          })
        }
        
        this.closeModal()
        this.loadWebhooks()
      } catch (error) {
        console.error('Error saving webhook:', error)
        this.$notify({
          title: 'Error',
          message: 'No se pudo guardar el webhook',
          type: 'error'
        })
      }
    },
    
    editWebhook(webhook) {
      this.currentWebhook = { ...webhook }
      this.showEditModal = true
      this.activeTab = 'config'
    },
    
    async deleteWebhook(webhookId) {
      if (!confirm('¿Estás seguro de que deseas eliminar este webhook?')) return
      
      try {
        await axios.delete(`/api/webhooks/${webhookId}`)
        this.$notify({
          title: 'Éxito',
          message: 'Webhook eliminado correctamente',
          type: 'success'
        })
        this.loadWebhooks()
      } catch (error) {
        console.error('Error deleting webhook:', error)
        this.$notify({
          title: 'Error',
          message: 'No se pudo eliminar el webhook',
          type: 'error'
        })
      }
    },
    
    async duplicateWebhook(webhook) {
      const duplicatedWebhook = { 
        ...webhook, 
        id: null,
        name: `${webhook.name} (Copia)`,
        authToken: this.generateRandomToken()
      }
      this.currentWebhook = duplicatedWebhook
      this.showCreateModal = true
      this.activeTab = 'config'
    },
    
    async testWebhook(webhook) {
      try {
        await axios.post(`/api/webhooks/${webhook.id}/test`)
        this.$notify({
          title: 'Éxito',
          message: 'Webhook ejecutado correctamente',
          type: 'success'
        })
        this.loadWebhooks()
      } catch (error) {
        console.error('Error testing webhook:', error)
        this.$notify({
          title: 'Error',
          message: 'Error al ejecutar el webhook',
          type: 'error'
        })
      }
    },
    
    async toggleWebhookStatus(webhook) {
      try {
        await axios.patch(`/api/webhooks/${webhook.id}/toggle`, {
          isActive: webhook.isActive
        })
        this.$notify({
          title: 'Éxito',
          message: `Webhook ${webhook.isActive ? 'activado' : 'desactivado'}`,
          type: 'success'
        })
      } catch (error) {
        console.error('Error toggling webhook status:', error)
        webhook.isActive = !webhook.isActive // Revert change
        this.$notify({
          title: 'Error',
          message: 'No se pudo cambiar el estado del webhook',
          type: 'error'
        })
      }
    },
    
    // Bulk actions
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedWebhooks = this.paginatedWebhooks.map(w => w.id)
      } else {
        this.selectedWebhooks = []
      }
    },
    
    async bulkActivate() {
      await this.bulkAction('activate', 'Webhooks activados')
    },
    
    async bulkDeactivate() {
      await this.bulkAction('deactivate', 'Webhooks desactivados')
    },
    
    async bulkDelete() {
      if (!confirm(`¿Estás seguro de eliminar ${this.selectedWebhooks.length} webhooks?`)) return
      await this.bulkAction('delete', 'Webhooks eliminados')
    },
    
    async bulkAction(action, successMessage) {
      try {
        await axios.post(`/api/webhooks/bulk-${action}`, {
          webhookIds: this.selectedWebhooks
        })
        this.$notify({
          title: 'Éxito',
          message: successMessage,
          type: 'success'
        })
        this.selectedWebhooks = []
        this.selectAll = false
        this.loadWebhooks()
      } catch (error) {
        console.error(`Error in bulk ${action}:`, error)
        this.$notify({
          title: 'Error',
          message: 'Error en la operación masiva',
          type: 'error'
        })
      }
    },
    
    // Modal actions
    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.currentWebhook = this.getEmptyWebhook()
      this.activeTab = 'config'
    },
    
    // Headers management
    addHeader() {
      this.currentWebhook.headers.push({ key: '', value: '' })
    },
    
    removeHeader(index) {
      this.currentWebhook.headers.splice(index, 1)
    },
    
    // Variables management
    addVariable() {
      this.currentWebhook.variables.push({
        name: '',
        type: 'string',
        description: '',
        required: false,
        defaultValue: ''
      })
    },
    
    removeVariable(index) {
      this.currentWebhook.variables.splice(index, 1)
    },
    
    // Utility methods
    generateAuthToken() {
      this.currentWebhook.authToken = this.generateRandomToken()
    },
    
    generateRandomToken() {
      return 'whk_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
    },
    
    formatDate(date) {
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getStatusBadgeClass(status) {
      switch (status) {
        case 'success': return 'badge-success'
        case 'error': return 'badge-danger'
        case 'timeout': return 'badge-warning'
        default: return 'badge-secondary'
      }
    },
    
    handlePageChange(page) {
      this.currentPage = page
    }
  }
}
</script>

<style scoped>
.webhooks-manager {
  padding: 0;
}

.page-header {
  margin-bottom: 1.5rem;
}

.modal.show {
  background-color: rgba(0,0,0,0.5);
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.table th {
  font-weight: 600;
  color: #344767;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.7rem;
}

.btn-group-sm > .btn {
  padding: 0.25rem 0.375rem;
}

code {
  font-size: 0.75rem;
  color: #e83e8c;
  background-color: #f8f9fa;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
}

.tab-content {
  min-height: 300px;
}

.form-check-label {
  cursor: pointer;
}

.alert {
  border: none;
  border-radius: 0.75rem;
}
</style> 